import { useEffect, useState } from 'react';
import ErrorSnackbar from '../errorSnackbar';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearError, clearSuccess, getPsd2LimitsConfig, putPsd2LimitsConfig } from '../../slices/psd2Controller';
import { Psd2LimitsConfig } from 'kent-identyum-service-api';
import { Alert, Snackbar } from '@mui/material';
import styles from './style.module.scss';
import { Roles } from '../../lib/constants/roles';
import { useAuth } from '../../hooks/useAuth';

interface NumericFieldDef {
    key: string;
    label: string;
    min: number;
    integerOnly?: boolean;
}

interface ListFieldDef {
    key: string;
    label: string;
}

interface BoolFieldDef {
    key: string;
    label: string;
}

interface SectionDef {
    title: string;
    numericFields: NumericFieldDef[];
    listFields: ListFieldDef[];
    boolFields?: BoolFieldDef[];
}

const sections: SectionDef[] = [
    {
        title: 'psd2_section_salary',
        numericFields: [
            { key: 'averagePay', label: 'psd2_min_avg_salary', min: 0, integerOnly: false },
            { key: 'salarySampleSize', label: 'psd2_no_of_salaries', min: 1 },
            { key: 'numberOfMonths', label: 'psd2_salary_months', min: 1 },
            { key: 'salaryDropThresholdPercent', label: 'psd2_salary_drop_threshold_percent', min: 0 },
            { key: 'maxDaysSinceLastSalary', label: 'psd2_max_days_since_last_salary', min: 1 },
        ],
        listFields: [],
        boolFields: [
            { key: 'includeSalaryAdditionsInSalary', label: 'psd2_include_salary_additions' },
        ],
    },
    {
        title: 'psd2_section_income',
        numericFields: [
            { key: 'bonusPeriodMonths', label: 'psd2_bonus_period_months', min: 1 },
            { key: 'bonusMinCount', label: 'psd2_bonus_min_count', min: 1 },
            { key: 'perDiemMinCount', label: 'psd2_per_diem_min_count', min: 1 },
            { key: 'pensionPeriodMonths', label: 'psd2_pension_period_months', min: 1 },
            { key: 'minPensionAmount', label: 'psd2_min_pension_amount', min: 0, integerOnly: false },
            { key: 'rentPeriodMonths', label: 'psd2_rent_period_months', min: 1 },
            { key: 'rentMinAmount', label: 'psd2_rent_min_amount', min: 0, integerOnly: false },
            { key: 'rentHaircutPercent', label: 'psd2_rent_haircut_percent', min: 0 },
        ],
        listFields: [],
    },
    {
        title: 'psd2_section_deductions',
        numericFields: [
            { key: 'obligationMinAmount', label: 'psd2_obligation_min_amount', min: 0, integerOnly: false },
            { key: 'obligationPeriodMonths', label: 'psd2_obligation_period_months', min: 1 },
        ],
        listFields: [
            { key: 'hrokLeasingCompanies', label: 'psd2_hrok_leasing_companies' },
        ],
    },
    {
        title: 'psd2_section_knockout',
        numericFields: [
            { key: 'gamblingPeriodMonths', label: 'psd2_gambling_period_months', min: 1 },
            { key: 'gamblingMaxSharePercent', label: 'psd2_gambling_max_share_percent', min: 0 },
            { key: 'cashflowPeriodMonths', label: 'psd2_cashflow_period_months', min: 1 },
            { key: 'cashflowGrowthThresholdPercent', label: 'psd2_cashflow_growth_threshold_percent', min: 0 },
        ],
        listFields: [],
    },
];

const allNumericFields = sections.flatMap((s) => s.numericFields);
const allListFields = sections.flatMap((s) => s.listFields);
const allBoolFields = sections.flatMap((s) => s.boolFields ?? []);

const PSD2Settings: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { userRoles } = useAuth();
    const { error, isLoading, isSaving, saveSuccess, config } = useAppSelector((state) => state.psd2Controller);
    const [values, setValues] = useState<Record<string, string>>({});
    const [boolValues, setBoolValues] = useState<Record<string, boolean>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
    const readOnly = !userRoles.includes(Roles.psd2SettingsWrite);

    const validateField = (value: string, min: number, badInput = false, integerOnly = true): string | undefined => {
        if (value === '') return t('validation_required');
        if (badInput || Number.isNaN(Number(value))) return t(integerOnly ? 'validation_integer' : 'validation_number');
        if (integerOnly && !Number.isInteger(Number(value))) return t('validation_integer');
        if (Number(value) < min) return t('validation_min_value', { min });
        return undefined;
    };

    const handleBlur = (
        field: NumericFieldDef,
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const input = e.target as HTMLInputElement;
        const error = validateField(input.value, field.min, input.validity.badInput, field.integerOnly !== false);
        setFieldErrors((prev) => ({ ...prev, [field.key]: error }));
    };

    const validate = (): boolean => {
        const errors: Record<string, string | undefined> = {};
        allNumericFields.forEach((field) => {
            errors[field.key] = validateField(values[field.key] ?? '', field.min, false, field.integerOnly !== false);
        });
        setFieldErrors(errors);
        return !Object.values(errors).some(Boolean);
    };

    useEffect(() => {
        dispatch(getPsd2LimitsConfig());
    }, [dispatch]);

    useEffect(() => {
        if (config) {
            const extended = config as unknown as Record<string, unknown>;
            const next: Record<string, string> = {};
            allNumericFields.forEach((field) => {
                const value = extended[field.key];
                next[field.key] = value === null || value === undefined ? '' : String(value);
            });
            allListFields.forEach((field) => {
                const value = extended[field.key];
                next[field.key] = Array.isArray(value) ? value.join(', ') : '';
            });
            const nextBool: Record<string, boolean> = {};
            allBoolFields.forEach((field) => {
                nextBool[field.key] = Boolean(extended[field.key]);
            });
            setValues(next);
            setBoolValues(nextBool);
        }
    }, [config]);

    useEffect(() => {
        if (error) {
            setTimeout(() => dispatch(clearError()), 4000);
        }
    }, [error]);

    useEffect(() => {
        if (saveSuccess) {
            setTimeout(() => dispatch(clearSuccess()), 4000);
        }
    }, [saveSuccess]);

    const handleSave = () => {
        if (!validate()) return;
        const payload: Record<string, unknown> = {};
        allNumericFields.forEach((field) => {
            payload[field.key] = field.integerOnly === false
                ? parseFloat(values[field.key])
                : parseInt(values[field.key], 10);
        });
        allListFields.forEach((field) => {
            payload[field.key] = (values[field.key] ?? '')
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item.length > 0);
        });
        allBoolFields.forEach((field) => {
            payload[field.key] = Boolean(boolValues[field.key]);
        });
        dispatch(putPsd2LimitsConfig(payload as unknown as Psd2LimitsConfig));
    };

    return (
        <div className={styles.root}>
            {error && <ErrorSnackbar message={error} />}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={saveSuccess}>
                <Alert severity="success">{t('save_success')}</Alert>
            </Snackbar>
            <Grid container direction="column" alignItems="start">
                {isLoading ? (
                    <div className="loaderMainDiv">
                        <div className="loaderBgDiv" />
                        <div className="loaderInnerDiv">
                            <div className="imgSize" style={{ position: 'relative' }}>
                                <CircularProgress color="secondary" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {sections.map((section) => (
                            <Grid container direction="column" alignItems="start" key={section.title}>
                                <Typography variant="h6" sx={{ marginTop: 3 }}>
                                    {t(section.title)}
                                </Typography>
                                {section.numericFields.map((field) => (
                                    <TextField
                                        key={field.key}
                                        name={field.key}
                                        onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                                        onBlur={(e) => handleBlur(field, e)}
                                        type="number"
                                        label={t(field.label)}
                                        value={values[field.key] ?? ''}
                                        disabled={isSaving || readOnly}
                                        error={!!fieldErrors[field.key]}
                                        helperText={fieldErrors[field.key]}
                                        inputProps={field.integerOnly === false ? { step: '0.01' } : undefined}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ maxWidth: 300, marginTop: 2 }}
                                    />
                                ))}
                                {(section.boolFields ?? []).map((field) => (
                                    <FormControlLabel
                                        key={field.key}
                                        sx={{ marginTop: 2 }}
                                        control={
                                            <Checkbox
                                                checked={boolValues[field.key] ?? false}
                                                onChange={(e) =>
                                                    setBoolValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                                                }
                                                disabled={isSaving || readOnly}
                                            />
                                        }
                                        label={t(field.label)}
                                    />
                                ))}
                                {section.listFields.map((field) => (
                                    <TextField
                                        key={field.key}
                                        name={field.key}
                                        onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                                        label={t(field.label)}
                                        value={values[field.key] ?? ''}
                                        disabled={isSaving || readOnly}
                                        multiline
                                        minRows={2}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: '100%', maxWidth: 600, marginTop: 2 }}
                                    />
                                ))}
                            </Grid>
                        ))}
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving || readOnly}
                            sx={{ marginTop: 4 }}
                        >
                            {isSaving ? <CircularProgress size={24} /> : t('save_btn')}
                        </Button>
                    </>
                )}
            </Grid>
        </div>
    );
};

export default PSD2Settings;
